import React, { useCallback, useEffect, useState } from 'react';
import type { ReviewEnrollmentViewProps } from 'interfaces/enrollments/ReviewEnrollmentViewProps';
import type { TeamReview } from 'models/projects/TeamReview';
import type { EnrollmentVoteRequest } from 'models/submissions/EnrollmentVoteRequest';
import { Vote } from 'models/projects/Vote';
import { TeamReviewDecision } from 'models/projects/TeamReviewDecision';
import { TeamReviewMemberOrderSetting } from 'models/teams/teamReview/TeamReviewMemberOrderSetting';
import { ApiService } from 'services/ApiService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const VOTE_LABEL: Record<Vote, string> = {
  [Vote.None]: 'Pending',
  [Vote.Pass]: 'Approved',
  [Vote.Deny]: 'Denied',
  [Vote.MoreInfoNeeded]: 'More Info Needed',
  [Vote.Skipped]: 'Skipped',
};

const VOTE_BADGE: Record<Vote, string> = {
  [Vote.None]: 'bg-gray-100 text-gray-500 border border-gray-300',
  [Vote.Pass]: 'bg-green-100 text-green-700 border border-green-300',
  [Vote.Deny]: 'bg-red-100 text-red-700 border border-red-300',
  [Vote.MoreInfoNeeded]: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
  [Vote.Skipped]: 'bg-gray-100 text-gray-500 border border-gray-300',
};

const DECISION_LABEL: Record<TeamReviewDecision, string> = {
  [TeamReviewDecision.NotYetStarted]: 'Not Started',
  [TeamReviewDecision.InProgress]: 'In Progress',
  [TeamReviewDecision.Approved]: 'Approved',
  [TeamReviewDecision.Denied]: 'Denied',
  [TeamReviewDecision.WaitingForUserSubmission]: 'Waiting',
};

const DECISION_BADGE: Record<TeamReviewDecision, string> = {
  [TeamReviewDecision.NotYetStarted]: 'bg-gray-100 text-gray-600',
  [TeamReviewDecision.InProgress]: 'bg-blue-100 text-blue-700',
  [TeamReviewDecision.Approved]: 'bg-green-100 text-green-700',
  [TeamReviewDecision.Denied]: 'bg-red-100 text-red-700',
  [TeamReviewDecision.WaitingForUserSubmission]: 'bg-yellow-100 text-yellow-700',
};

const isReviewFinished = (d: TeamReviewDecision): boolean =>
  d === TeamReviewDecision.Approved ||
  d === TeamReviewDecision.Denied;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ReviewEnrollmentView: React.FC<ReviewEnrollmentViewProps> = ({
  enrollment,
  nodeInstance,
  isDisabled = false,
  onReviewComplete,
}) => {
  const currentUserId = localStorage.getItem('userId') ?? '';

  const [teamReview, setTeamReview] = useState<TeamReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [voteError, setVoteError] = useState<string | null>(null);
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);
  const [moreInfoText, setMoreInfoText] = useState('');

  // ---------------------------------------------------------------------------
  // Load team review
  // ---------------------------------------------------------------------------
  const loadTeamReview = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const review = await ApiService.getEnrollmentTeamReview(enrollment.id);
      setTeamReview(review);
    } catch {
      setFetchError('Failed to load team review. Please try refreshing.');
    } finally {
      setLoading(false);
    }
  }, [enrollment.id]);

  useEffect(() => { void loadTeamReview(); }, [loadTeamReview]);

  // ---------------------------------------------------------------------------
  // Vote handler
  // ---------------------------------------------------------------------------
  const handleVote = async (vote: Vote, decisionReason?: string) => {
    if (!teamReview) return;
    setSubmitting(true);
    setVoteError(null);
    try {
      const request: EnrollmentVoteRequest = {
        teamMemberId: currentUserId,
        vote,
        decisionReason: decisionReason ?? '',
        workflowInstanceId: nodeInstance.workflowInstanceId,
        nodeInstanceId: nodeInstance.id,
      };
      const result = await ApiService.submitEnrollmentVote(enrollment.id, request);
      if (result.reviewComplete) {
        onReviewComplete?.();
      } else {
        await loadTeamReview();
      }
    } catch (e: any) {
      setVoteError(e?.response?.data?.detail ?? e?.message ?? 'Failed to submit vote. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMoreInfoSubmit = async () => {
    await handleVote(Vote.MoreInfoNeeded, moreInfoText.trim());
    setMoreInfoOpen(false);
    setMoreInfoText('');
  };

  const handleMoreInfoCancel = () => {
    setMoreInfoOpen(false);
    setMoreInfoText('');
  };

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="mt-3 space-y-2" aria-label="Loading team review">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
        ))}
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="mt-3 space-y-2">
        <p className="text-sm text-red-600">{fetchError}</p>
        <button
          type="button"
          onClick={() => void loadTeamReview()}
          className="text-xs text-blue-600 hover:underline focus:outline-none"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!teamReview) return null;

  const settings = teamReview.teamReviewSettings;
  const isSequential =
    settings?.teamReviewMemberOrderSetting === TeamReviewMemberOrderSetting.Sequential;
  const isFinished = isReviewFinished(teamReview.decision);

  // Helper: look up first/last name from the settings member order list
  const getMemberName = (memberId: string): string => {
    const entry = settings?.teamMemberOrder?.find(m => m.teamMemberId === memberId);
    if (entry) {
      const name = `${entry.firstName ?? ''} ${entry.lastName ?? ''}`.trim();
      if (name) return name;
    }
    return 'Team Member';
  };

  // Sorted votes by position
  const sortedVotes = [...(teamReview.memberVotes ?? [])].sort(
    (a, b) => a.position - b.position,
  );

  // For sequential ordering: the next member who hasn't voted yet
  const nextPendingMemberId = isSequential
    ? sortedVotes.find(v => v.vote === Vote.None)?.teamMemberId ?? null
    : null;

  // ---------------------------------------------------------------------------
  // Results panel (review finished)
  // ---------------------------------------------------------------------------
  if (isFinished) {
    return (
      <div className="mt-3 rounded-md border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-800">Review Complete</h4>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${DECISION_BADGE[teamReview.decision]}`}
          >
            {DECISION_LABEL[teamReview.decision]}
          </span>
        </div>

        <ul className="space-y-2" aria-label="Member votes">
          {sortedVotes.map(v => (
            <li key={v.teamMemberId} className="flex items-center justify-between text-sm">
              <span className="text-gray-700">{getMemberName(v.teamMemberId)}</span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${VOTE_BADGE[v.vote]}`}
              >
                {VOTE_LABEL[v.vote]}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Active voting panel
  // ---------------------------------------------------------------------------
  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-white p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800">Team Review</h4>
        <button
          type="button"
          onClick={() => void loadTeamReview()}
          disabled={loading || submitting}
          aria-label="Refresh votes"
          className="text-xs text-blue-600 hover:underline disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
        >
          ↻ Refresh
        </button>
      </div>

      {/* Settings summary */}
      {settings && (
        <p className="text-xs text-gray-500">
          {settings.teamReviewApprovalSetting === 0 ? 'All members must approve' : 'Any member approval is sufficient'}
          {isSequential ? ' · votes must be cast in order' : ''}
        </p>
      )}

      {/* Member rows */}
      <ul className="space-y-2" aria-label="Member votes">
        {sortedVotes.map(v => {
          const isMe = v.teamMemberId === currentUserId;
          const hasVoted = v.vote !== Vote.None;
          const isMyTurn = !isSequential || nextPendingMemberId === currentUserId;
          const canVoteNow = isMe && !hasVoted && isMyTurn;
          const waitingForPrior = isMe && !hasVoted && !isMyTurn;

          return (
            <li key={v.teamMemberId} className="space-y-1.5">
              {/* Name + current vote badge */}
              <div className="flex items-center justify-between text-sm">
                <span className={`font-medium ${isMe ? 'text-blue-700' : 'text-gray-700'}`}>
                  {getMemberName(v.teamMemberId)}
                  {isMe && <span className="ml-1 text-xs text-gray-400">(you)</span>}
                </span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${VOTE_BADGE[v.vote]}`}
                >
                  {VOTE_LABEL[v.vote]}
                </span>
              </div>

              {/* Voting buttons — only for the current user if eligible */}
              {canVoteNow && (
                <div className="flex gap-2 flex-wrap pl-1">
                  {[
                    { vote: Vote.Pass, label: 'Approve', cls: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500' },
                    { vote: Vote.Deny, label: 'Deny', cls: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500' },
                  ].map(({ vote: voteOption, label, cls }) => (
                    <button
                      key={voteOption}
                      type="button"
                      onClick={() => void handleVote(voteOption)}
                      disabled={submitting || isDisabled}
                      className={`inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-1 ${cls}`}
                    >
                      {submitting ? '…' : label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setMoreInfoOpen(true)}
                    disabled={submitting || isDisabled}
                    className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium bg-yellow-500 hover:bg-yellow-600 text-white focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-yellow-400 disabled:opacity-50"
                  >
                    More Info
                  </button>
                </div>
              )}

              {/* Waiting-for-prior message */}
              {waitingForPrior && (
                <p className="pl-1 text-xs text-yellow-600 italic">
                  Waiting for prior member to vote before your turn.
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {/* Vote error */}
      {voteError && (
        <p role="alert" className="text-xs text-red-600">{voteError}</p>
      )}

      {/* More Info dialog */}
      {moreInfoOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="more-info-dialog-title"
        >
          <div className="w-full max-w-sm rounded-xl bg-white shadow-xl p-5 space-y-4 mx-4">
            <div>
              <h3 id="more-info-dialog-title" className="text-sm font-semibold text-gray-900">
                More Information Needed
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Describe what details are required for a successful review.
              </p>
            </div>

            <textarea
              value={moreInfoText}
              onChange={e => setMoreInfoText(e.target.value)}
              rows={4}
              placeholder="e.g. Please provide the dimensions of the tattoo (length × width), placement on the body, colour palette, and any reference images or style details…"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 resize-none"
              autoFocus
            />

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleMoreInfoCancel}
                disabled={submitting}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleMoreInfoSubmit()}
                disabled={submitting || moreInfoText.trim().length === 0}
                className="rounded-lg px-3 py-1.5 text-xs font-medium bg-yellow-500 hover:bg-yellow-600 text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:opacity-50"
              >
                {submitting ? '…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewEnrollmentView;

